var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/*
 * <vitesse-run-tree> — the agent tree, on screen at last.
 *
 * One row per run, indented by depth, in spawn order. It reads a `RunTree` and renders it; it holds
 * no state but which rows are expanded, and it fetches nothing (the Cardinal Rule).
 *
 * THIS IS THE TRACE VIEW — one of two, beside the Pipes canvas (`run-pipes-view`).
 *
 * It used to argue that rows were the right and only shape, on the grounds that indentation already
 * states what edges would draw. That was wrong about what a reader is doing: indentation states who
 * spawned whom, and the question is a PIPELINE — what each node was asked for, what it owes back,
 * and where its answer goes when it lands. The canvas draws that; these rows are the better
 * instrument for the other question, "what happened, in order", which a chronological list answers
 * and a canvas does not. Two views over one tree, each doing the thing it is good at.
 *
 * The visual grammar is deliberately the tool row's: a status mark, a name, a live line of prose,
 * and a disclosure. A turn can show tool calls AND a subtree, and inventing a second idiom for the
 * second one is how a transcript starts reading as two designs stacked.
 */
import { LitElement, html, nothing, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { t } from '../i18n';
import { LocaleController } from '../i18n/LocaleController';
import { runState, runLabel, runElapsed as elapsed, runCost as cost, FINISHED, RUNNING, AWAITING, COMPLETED } from './run-format';
import '../elements/vitesse-icon';
let VitesseRunTree = class VitesseRunTree extends LitElement {
    constructor() {
        super(...arguments);
        this.open = new Set();
        // eslint-disable-next-line @typescript-eslint/no-unused-vars -- re-renders on locale change
        this.locale = new LocaleController(this);
        this.onChange = () => {
            // Restart on the way back UP as well as stopping on the way down: a tree that was idle when this
            // mounted (or that finished and then spawned again) would otherwise never get its clock back.
            if (this.tree?.active)
                this.startTicking();
            this.requestUpdate();
        };
        /*
         * A SECOND HAND, so the elapsed time reads as a clock rather than as a poll.
         *
         * runElapsed already computes against Date.now(), and the tree re-reads every 2s, so the figure
         * did advance — but only when a network round-trip landed, so it moved in jumps of whatever the
         * round-trip happened to cost and sat still in between. A duration that stalls is read as a run
         * that has stalled, which is the one thing it must not say while an agent is working.
         *
         * A local timer costs nothing and is not a second source of truth: the VALUE still comes from the
         * run's own start and end stamps. This only decides how often it is drawn. It runs while the tree
         * is active and stops when it is not, so a finished tree — a document — is not repainted forever.
         */
        this.ticker = null;
    }
    startTicking() {
        if (this.ticker)
            return;
        this.ticker = setInterval(() => {
            if (this.tree?.active)
                this.requestUpdate();
            else
                this.stopTicking();
        }, 1000);
    }
    stopTicking() {
        if (!this.ticker)
            return;
        clearInterval(this.ticker);
        this.ticker = null;
    }
    connectedCallback() {
        super.connectedCallback();
        this.tree?.addEventListener('change', this.onChange);
        this.startTicking();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        // The subscription, the poll AND the ticker. Anything left running after its view is gone is a
        // leak that only ever shows up as traffic, or wakeups, nobody can account for.
        this.tree?.removeEventListener('change', this.onChange);
        this.tree?.stop();
        this.stopTicking();
    }
    static { this.styles = css `
    :host {
      display: block;
      font-size: 12px;
      /* The tree hangs off the tool row that created it, so it is indented to read as that row's
         consequence rather than as a sibling block. */
      margin: 2px 0 2px 4px;
      border-left: 2px solid var(--border-color);
      padding-left: 8px;
    }
    .row {
      display: flex;
      align-items: center;
      gap: 6px;
      width: 100%;
      background: none;
      border: 0;
      padding: 1px 0;
      margin: 0;
      font: inherit;
      color: var(--text-secondary);
      cursor: pointer;
      text-align: left;
    }
    .row:hover {
      color: var(--text-primary);
    }
    .mark {
      display: inline-flex;
      width: 13px;
      flex-shrink: 0;
      color: var(--text-secondary);
    }
    .running .mark {
      color: var(--blue);
      animation: spin 1.4s linear infinite;
    }
    .done .mark {
      color: var(--green);
    }
    .stopped .mark {
      color: var(--red);
    }
    .awaiting .mark {
      color: var(--yellow, var(--text-secondary));
    }
    @keyframes spin {
      to {
        transform: rotate(360deg);
      }
    }
    .label {
      color: var(--text-primary);
      font-weight: 500;
      flex-shrink: 0;
    }
    .stopped .label {
      color: var(--red);
    }
    /* The headline is the one line that changes while you watch, so it gets the room and truncates
       rather than wrapping — a row that grows a line every few seconds makes the whole list jump. */
    .headline {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .meta {
      flex-shrink: 0;
      color: var(--text-tertiary);
      font-variant-numeric: tabular-nums;
    }
    /* Stale means its inputs changed and the held result is queued for replacement. Worth showing:
       the number on screen is real but superseded, which is different from wrong and from current. */
    .stale .headline {
      opacity: 0.6;
      font-style: italic;
    }
    /* The detail is a definition list, not a stack of paragraphs — label and value on consecutive
       lines, tight, so opening a node adds a few lines rather than a screen. */
    .body {
      margin: 1px 0 4px 6px;
      padding-left: 8px;
      border-left: 1px solid var(--border-color);
      color: var(--text-secondary);
      line-height: 1.4;
      word-break: break-word;
    }
    /*
     * pre-wrap BELONGS ON THE VALUE, NOT ON THE BLOCK.
     *
     * On the container it preserved the template's own newlines and indentation — every line break
     * between a <dt> and its <dd> rendered as blank space, so a three-line detail took most of a
     * panel and the rows read as though they had been spaced out on purpose. It is here for
     * multi-line objectives, which are the values; the layout around them is not text.
     */
    .body dd {
      margin: 0 0 4px;
      white-space: pre-wrap;
    }
    .body dt {
      color: var(--text-tertiary);
      margin-top: 2px;
      font-size: 10.5px;
      font-weight: 600;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      opacity: 0.8;
    }
    .body dd:last-of-type {
      margin-bottom: 0;
    }
    .enter {
      background: none;
      border: 0;
      padding: 1px 0;
      margin-top: 4px;
      font: inherit;
      font-size: 12px;
      color: var(--blue);
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      gap: 4px;
    }
    .err {
      color: var(--red);
      padding: 4px 0;
    }
    @media (prefers-reduced-motion: reduce) {
      .running .mark {
        animation: none;
      }
    }
  `; }
    render() {
        const tree = this.tree;
        if (!tree)
            return nothing;
        // Nothing at all is the common case — most turns spawn no agents — and an empty box explaining
        // that would be chrome on every conversation that never needed it.
        if (!tree.roots.length && !tree.error)
            return nothing;
        return html ` ${tree.error ? html `<div class="err">${tree.error}</div>` : nothing} ${tree.roots.map((n) => this.row(n))} `;
    }
    row(node, depth = 0) {
        const run = node.run;
        const open = this.open.has(run.runId);
        // The state and the name come from run-format, shared with the Pipes canvas — two views
        // disagreeing about whether a budget-capped run is "done" or "stopped" is the drift this
        // avoids, and it is the one reading of a run that must never differ between surfaces.
        const state = runState(run);
        const mark = state === 'running' ? 'orbit' : state === 'done' ? 'check' : state === 'stopped' ? 'x' : 'zap';
        const label = runLabel(run);
        return html `
      <div class="${state} ${run.stale ? 'stale' : ''}" style="padding-left:${depth * 14}px">
        <button class="row" aria-expanded=${open ? 'true' : 'false'} @click=${() => this.toggle(run.runId)}>
          <span class="mark"><vitesse-icon name=${mark} size="13"></vitesse-icon></span>
          <span class="label" title=${run.expertId}>${label}</span>
          <span class="headline">${run.headline || run.task?.objective || ''}</span>
          <span class="meta">${elapsed(run)}${cost(run)}</span>
        </button>
        ${open ? this.detail(node) : nothing}
      </div>
      ${node.children.map((c) => this.row(c, depth + 1))}
    `;
    }
    detail(node) {
        const run = node.run;
        return html `
      <dl class="body">
        ${run.task?.objective
            ? html `<dt>${t('ai.runObjective')}</dt>
                <dd>${run.task.objective}</dd>`
            : nothing}
        ${run.stale && run.staleBecause
            ? html `<dt>${t('ai.runStale')}</dt>
                <dd>${run.staleBecause}</dd>`
            : nothing}
        <dt>${t('ai.runModel')}</dt>
        <dd>${run.model || '—'}${run.effort ? ` · ${run.effort}` : ''} · ${t('ai.runSteps', { count: run.stepCount })}</dd>
        <!--
          The conversation is the point. Every run owns one, which is what makes "go and read what
          this agent is actually saying" a link rather than a feature to build: the transcript is
          already there, addressed by id, in the same store the top-level thread uses.
        -->
        ${this.onOpenRun && run.conversationId
            ? html `<button class="enter" @click=${() => this.onOpenRun?.(run.runId, run.conversationId)}>
                <vitesse-icon name="message-circle" size="13"></vitesse-icon>${t('ai.runOpenChat')}
              </button>`
            : nothing}
      </dl>
    `;
    }
    toggle(runId) {
        // Keyed by run id, not by object: a row is replaced on every poll, so an identity-keyed set
        // would close every open row two seconds after it was opened.
        const next = new Set(this.open);
        if (!next.delete(runId))
            next.add(runId);
        this.open = next;
    }
};
__decorate([
    property({ attribute: false })
], VitesseRunTree.prototype, "tree", void 0);
__decorate([
    property({ attribute: false })
], VitesseRunTree.prototype, "onOpenRun", void 0);
__decorate([
    state()
], VitesseRunTree.prototype, "open", void 0);
VitesseRunTree = __decorate([
    customElement('vitesse-run-tree')
], VitesseRunTree);
export { VitesseRunTree };
//# sourceMappingURL=run-tree-view.js.map